import type { HeadData } from "./seo-meta";

/**
 * Applique un HeadData au document. Utilisé à chaque changement de route pour que
 * le <head> rendu côté client reste identique à celui prérendu au build.
 */

const ROBOTS_INDEX = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
const ROBOTS_NOINDEX = "noindex, follow";

function setMeta(attr: "name" | "property", key: string, content: string) {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function removeMeta(attr: "name" | "property", key: string) {
  document.head.querySelector(`meta[${attr}="${key}"]`)?.remove();
}

function setLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let tag = document.head.querySelector<HTMLLinkElement>(selector);
  if (!tag) {
    tag = document.createElement("link");
    tag.rel = rel;
    if (hreflang) tag.hreflang = hreflang;
    document.head.appendChild(tag);
  }
  tag.href = href;
}

export function applyHead(head: HeadData) {
  document.title = head.title;

  setMeta("name", "description", head.description);
  setMeta("name", "robots", head.noindex ? ROBOTS_NOINDEX : ROBOTS_INDEX);

  setMeta("property", "og:type", head.type);
  setMeta("property", "og:site_name", "Ma voix pour de Villepin");
  setMeta("property", "og:locale", "fr_FR");
  setMeta("property", "og:url", head.canonical);
  setMeta("property", "og:title", head.title);
  setMeta("property", "og:description", head.description);
  setMeta("property", "og:image", head.image);
  setMeta("property", "og:image:alt", head.imageAlt);
  setMeta("property", "og:image:width", "1200");
  setMeta("property", "og:image:height", "630");

  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", head.title);
  setMeta("name", "twitter:description", head.description);
  setMeta("name", "twitter:image", head.image);

  if (head.published) setMeta("property", "article:published_time", head.published);
  else removeMeta("property", "article:published_time");
  if (head.modified) setMeta("property", "article:modified_time", head.modified);
  else removeMeta("property", "article:modified_time");

  setLink("canonical", head.canonical);
  setLink("alternate", head.canonical, "fr-FR");
  setLink("alternate", head.canonical, "x-default");

  // On retire aussi les blocs prérendus (sans attribut) pour éviter les doublons
  // après hydratation : le head doit contenir exactement un jeu de données.
  document.head.querySelectorAll('script[type="application/ld+json"]').forEach((node) => node.remove());
  for (const data of head.jsonLd) {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo", "jsonld");
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
  }
}
